import React from 'react';
import {BaseSettingsForm, SelectField} from '@/components/forms';
import {Form, Row, Col} from 'react-bootstrap';
import {Button} from '@/components/ui';
import {Tooltip} from '@/components/tooltips';
import {useStoreActions, useStoreState} from 'easy-peasy';
import TextareaAutosize from 'react-textarea-autosize';
import EventFiltersBundle from '@/components/event_filters/EventFiltersBundle';
import QueryHelpModal from '@/components/utils/QueryHelpModal';
import * as yup from 'yup';
import i18n from 'i18n';
import Icon from '@/components/icons/Icon';
import {buildURL} from '@/utils/urlBuilder';

interface Rule {
    id: number;
    title: string;
    active: boolean;
    actionId: number;
    conditionKontinue: boolean;
}

interface Action {
    id: number;
    title: string;
}

interface RuleSettingsFormProps {
    ruleId?: number;
    channelId?: number;
    onDone: () => void;
}

interface FormValues {
    title: string;
    active: boolean;
    actionId: number;
    conditionKontinue: boolean;
}

const RuleSettingsFormSchema = yup.object({
    title: yup.string()
        .max(64, i18n.t('jt_rule_title_max.text'))
        .required(i18n.t('jt_rule_title_required.text')),
});

const RuleSettingsForm: React.FC<RuleSettingsFormProps> = React.memo((
    {
        ruleId = 0,
        channelId = undefined,
        onDone,
    },
) => {
    const rules = useStoreState((state: any) => state.rules.items) as Rule[];
    const actionsList = useStoreState((state: any) => state.actions.items) as Action[];
    const addOrEditRule = useStoreActions((actions: any) => actions.rules.addOrEditRule);

    // Get default action from URL params or first available action
    let defaultAction = actionsList[0]?.id || 1;
    const urlParams = new URLSearchParams(window.location.search);
    const actionId = urlParams.get('addrule');
    if (actionId) {
        defaultAction = parseInt(actionId, 10);
    }

    const initialValues: FormValues = rules.find((rule) => rule.id === ruleId) || {
        title: '',
        active: true,
        actionId: defaultAction,
        conditionKontinue: true,
    };

    const customSubmissionHandler = async (values: FormValues) => {
        const response = await fetch(ruleId ? buildURL('ruleDetails', {rule_id: ruleId}) : window.URLS.rulesResource, {
            method: ruleId ? 'PUT' : 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...values,
                channelId,
            }),
        });

        if (!response.ok) {
            throw new Error('Failed to save rule');
        }

        const data = await response.json();

        onDone();
        addOrEditRule(data);

        return {data};
    };

    return (
        <>
            <BaseSettingsForm
                entityId={ruleId || undefined}
                resourceUrl="" // Not used with customSubmissionHandler
                storeActionPath="" // Not used with customSubmissionHandler
                validationSchema={RuleSettingsFormSchema}
                initialValues={initialValues}
                successMessageKey="jt_save_filters.text"
                customSubmissionHandler={customSubmissionHandler}
            >
                {(formik, loading) => (
                    <>
                        <Row>
                            <Col xs={6}>
                                <Form.Group className="mb-3" controlId="ruleTitle" >
                                    <Form.Label className='d-flex align-items-center'>
                                        {i18n.t('jt_rule_name.text')}
                                        <Tooltip
                                            content={i18n.t('tt_set_rule.text')}
                                            placement="top"
                                        >
                                            <Icon name="mdi-information-outline" size="xs" className='text-info mx-2' />
                                        </Tooltip>
                                    </Form.Label>
                                    <Form.Control
                                        as={TextareaAutosize}
                                        name="title"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.title}
                                        isInvalid={formik.touched.title && !!formik.errors.title}
                                        autoFocus
                                        data-cy="rule-settings-name-input"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formik.errors.title}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={6}>
                                <Form.Group className="mb-3" controlId="action" >
                                    <Form.Label className='d-flex align-items-center'>
                                        {i18n.t('jt_action.text')}
                                        <Tooltip
                                            content={i18n.t('tt_choose_the.text')}
                                            placement="top"
                                        >
                                            <Icon name="mdi-information-outline" size="xs" className='text-info mx-2' />
                                        </Tooltip>
                                    </Form.Label>
                                    <SelectField
                                        name="actionId"
                                        value={actionsList.find((x) => x.id === formik.values.actionId) ? {
                                            value: formik.values.actionId,
                                            label: actionsList.find((x) => x.id === formik.values.actionId)?.title || ''
                                        } : null}
                                        onChange={(value) => formik.setFieldValue('actionId', value?.value || 0)}
                                        options={actionsList.map((obj) => ({
                                            value: obj.id,
                                            label: obj.title,
                                        }))}
                                        isRequired={true}
                                        error={formik.errors.actionId}
                                        touched={formik.touched.actionId}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={6}>
                                <Form.Group className="mb-3" controlId="kontinue" >
                                    <Form.Label className='d-flex align-items-center'>
                                        {i18n.t('jt_continue.text')}
                                        <Tooltip
                                            content={i18n.t('tt_rule_contains.text')}
                                            placement="top"
                                        >
                                            <Icon name="mdi-information-outline" size="xs" className='text-info mx-2' />
                                        </Tooltip>
                                    </Form.Label>
                                    <SelectField
                                        name="conditionKontinue"
                                        value={{
                                            value: formik.values.conditionKontinue,
                                            label: formik.values.conditionKontinue ? 'Yes' : 'No',
                                        }}
                                        onChange={(value) => formik.setFieldValue('conditionKontinue', value?.value || false)}
                                        options={[
                                            {label: 'Yes', value: true},
                                            {label: 'No', value: false},
                                        ]}
                                        isRequired={true}
                                        error={formik.errors.conditionKontinue}
                                        touched={formik.touched.conditionKontinue}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3" controlId="active" >
                                    <Form.Label> {i18n.t('jt_active.text')}</Form.Label>
                                    <Form.Check
                                        name="active"
                                        label={i18n.t('jt_filters_status_label.text')}
                                        type="switch"
                                        onChange={formik.handleChange}
                                        checked={formik.values.active}
                                        isInvalid={!!formik.errors.active}
                                        feedback={formik.errors.active}
                                        id="channelActive"
                                    />
                                </Form.Group>
                            </Col >
                            <Col xs={6}>
                                <EventFiltersBundle
                                    formValues={formik.values}
                                    setValues={formik.setValues}
                                    setFieldValue={formik.setFieldValue}
                                    allFieldsName="allFields"
                                    requestParams={{
                                        params: {
                                            includeTextSearchFilters: true,
                                        },
                                    }}
                                    parent="rule-settings-form"
                                    oneFilterColSize={12}
                                    enableCollapse={false}
                                />
                            </Col>
                        </Row >

                        <Row className="mt-4">
                            <Col xs={12} className="d-flex justify-content-end">
                                <Button
                                    type="submit"
                                    variant="primary"
                                    disabled={formik.isSubmitting}
                                    loading={formik.isSubmitting}
                                    data-cy="rule-settings-save-btn"
                                >
                                    {ruleId ? i18n.t('jt_update.text') : i18n.t('jt_save.text')}
                                </Button>
                            </Col>
                        </Row>
                    </>
                )}
            </BaseSettingsForm >

            <QueryHelpModal />
        </>
    );
});


export default RuleSettingsForm; 